/**
 * One collapsible preset group (card + header + body)
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC, ReactNode } from 'react';

import { cn } from '@/lib/utils';

import type { Preset } from '../shared';
import { sumPresetTemplateDrops } from './shared';
import { groupStartSortableId, SortableGroupHeaderRow } from './sortable';

export interface PresetGroupSectionProps {
  group: { label: string; firstPresetId: string; items: Preset[] };
  expanded: boolean;
  onToggleExpand: () => void;
  onApplyGroup?: (presetIds: string[], matchListOrder: boolean) => void;
  bulkMatchListOrder: boolean;
  applyDisabled: boolean;
  groupNeedsRouting?: boolean;
  bulkCampaignOk?: boolean;
  bulkRoutingFilled?: boolean;
  selectionMode?: boolean;
  bulkSelected?: Set<string>;
  onToggleGroupBulk?: (presetIds: string[]) => void;
  children: ReactNode;
}

export const PresetGroupSection: FC<PresetGroupSectionProps> = ({
  group,
  expanded,
  onToggleExpand,
  onApplyGroup,
  bulkMatchListOrder,
  applyDisabled,
  groupNeedsRouting = false,
  bulkCampaignOk = true,
  bulkRoutingFilled = true,
  selectionMode = false,
  bulkSelected,
  onToggleGroupBulk,
  children,
}) => {
  const ids = group.items.map((p) => p.id);
  const nSel = ids.filter((id) => bulkSelected?.has(id)).length;
  const groupBulkChecked =
    nSel === 0 ? false : nSel === ids.length ? true : ('indeterminate' as const);
  const templateDrops = sumPresetTemplateDrops(group.items);

  return (
    <section
      className="overflow-hidden rounded-xl border border-border/50 bg-card/50 shadow-sm"
      aria-label={`Group ${group.label}`}
    >
      <SortableGroupHeaderRow
        id={groupStartSortableId(group.firstPresetId)}
        label={group.label}
        templateDropCount={templateDrops}
        presetCount={group.items.length}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        onApplyGroup={
          onApplyGroup && group.items.length > 0
            ? () => onApplyGroup(ids, bulkMatchListOrder)
            : undefined
        }
        applyDisabled={applyDisabled}
        applyGroupTooltip={
          !bulkCampaignOk
            ? 'Choose a campaign in the bulk bar first.'
            : groupNeedsRouting && !bulkRoutingFilled
              ? 'Choose a gateway and short-link TLD in the bulk bar for rule-based presets in this group.'
              : 'Create prepared drops for every preset in this group'
        }
        bulkSelection={selectionMode}
        groupBulkChecked={groupBulkChecked}
        onToggleGroupBulk={
          onToggleGroupBulk && ids.length > 0 ? () => onToggleGroupBulk(ids) : undefined
        }
      />
      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out',
          expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
        aria-hidden={!expanded}
      >
        <div className="min-h-0 overflow-hidden border-t border-border/40 bg-background/30">
          <div className="flex flex-col gap-1.5 p-2 sm:p-2.5">{children}</div>
        </div>
      </div>
    </section>
  );
};
